import { createContext, useContext, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientAuthContext = createContext(null);

// FastAPI 422 returns an array of {msg} objects — always map detail to a string.
export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function ClientAuthProvider({ children }) {
  // undefined = still checking session, null = signed out, object = signed in
  const [client, setClient] = useState(undefined);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => setClient(r.data))
      .catch(() => setClient(null));
  }, []);

  const login = async (email, password) => {
    const { data } = await axios.post(
      `${API}/auth/login`,
      { email, password },
      { withCredentials: true },
    );
    setClient(data);
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      /* session already gone — fine */
    }
    setClient(null);
  };

  return (
    <ClientAuthContext.Provider
      value={{ client, checking: client === undefined, login, logout }}
    >
      {children}
    </ClientAuthContext.Provider>
  );
}

export const useClientAuth = () => useContext(ClientAuthContext);

// Gate for client-only tools (Checklist, Equipment, Certification).
export function RequireClient({ children }) {
  const { client, checking } = useClientAuth();
  const location = useLocation();

  if (checking) {
    return (
      <div
        data-testid="client-auth-loading"
        className="min-h-[60vh] flex items-center justify-center"
      >
        <Loader2 size={28} className="animate-spin text-[#FF007F]" />
      </div>
    );
  }
  if (!client) {
    return (
      <Navigate
        to={`/login?next=${encodeURIComponent(location.pathname)}`}
        replace
      />
    );
  }
  return children;
}
